import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Search,
  Briefcase,
  Users,
  Building2,
  Star,
  Lightbulb,
  Mail,
  Settings,
  Activity,
  EyeOff,
  LogOut,
  Zap,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import ThemeToggle from "../ThemeToggle";

const navItems = [
  { path: "/dashboard", label: "Command", icon: LayoutDashboard, exact: true },
  { path: "/discover", label: "Discover", icon: Search },
  { path: "/deal-rooms", label: "Deal Rooms", icon: Briefcase },
  { path: "/watchlist", label: "Watchlist", icon: Star },
  { path: "/theses", label: "Theses", icon: Lightbulb },
  { path: "/outreach", label: "Outreach", icon: Mail },
  { path: "/investors", label: "Investors", icon: Users },
  { path: "/vc-funds", label: "VC Funds", icon: Building2 },
  { path: "/stealth", label: "Stealth", icon: EyeOff },
  { path: "/ops", label: "Ops", icon: Activity },
  { path: "/settings", label: "Settings", icon: Settings },
];

export const Sidebar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();

  return (
    <aside className="fixed left-0 top-0 z-50 flex h-screen w-64 flex-col border-r border-zinc-800 bg-black/90 backdrop-blur-xl">
      <div className="border-b border-zinc-800 px-6 py-5">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-white text-zinc-900 shadow-sm">
            <Zap className="h-6 w-6" />
          </div>
          <div className="min-w-0">
            <h1 className="truncate text-zinc-50 font-medium tracking-tight font-['Outfit']">Venture Copilot</h1>
            <p className="text-xs text-zinc-500 font-mono">Intelligence Terminal</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto space-y-0.5 p-4">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = item.exact
            ? location.pathname === item.path
            : location.pathname === item.path || location.pathname.startsWith(item.path + "/");

          return (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
              className={`group flex items-center gap-3 rounded-md px-3 py-2.5 transition-all duration-200 ${
                isActive ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-50"
              }`}
            >
              <Icon className={`h-4 w-4 flex-shrink-0 ${isActive ? "text-zinc-900" : "text-zinc-500 group-hover:text-zinc-300"}`} />
              <span className="text-sm font-['Manrope']">{item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      <div className="border-t border-zinc-800 p-4">
        {user && (
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-zinc-800 text-xs font-mono text-zinc-400">
              {user.name?.charAt(0) || "V"}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm text-zinc-50 font-['Manrope']">{user.name}</p>
              <p className="truncate text-xs text-zinc-500 font-mono">{user.email}</p>
            </div>
            <button
              type="button"
              onClick={logout}
              data-testid="logout-btn"
              className="rounded p-1.5 text-zinc-500 transition-colors hover:bg-zinc-800 hover:text-zinc-300"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
};

export const MainLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-zinc-950">
      <Sidebar />
      <main className="ml-64 min-h-screen">
        <header className="sticky top-0 z-40 flex h-14 items-center justify-end border-b border-zinc-800 bg-zinc-950/80 px-6 backdrop-blur-xl">
          <ThemeToggle />
        </header>
        <div>{children}</div>
      </main>
    </div>
  );
};
